/**
 * Etiket tasarımını yazıcının dot matrisine çevirir.
 *
 * Akış: labelCanvasRenderer → eşikleme → mürekkep matrisi → döndürme.
 *
 * Eskiden etiket DOM'a kurulup html2canvas ile rasterlenirdi ("önizlemede
 * görülen ile basılan aynı olsun" gerekçesiyle). Ölçüm bunun bedelini
 * gösterdi: etiket başına ~4,5 saniye, yani 100 etiketlik bir baskıda dakikalar
 * (gerekçe ve sayılar için bkz. labelCanvasRenderer başlığı). Artık çizim
    10| * doğrudan canvas'a yapılıyor ve etiket başına birkaç milisaniye sürüyor.
 *
 * Neden döndürme piksel seviyesinde: tasarım döndürülmemiş halde rasterlenip
 * matris burada çevriliyor — PowerShell prototipinde GDI+ `RotateFlipType` ile
 * doğrulanan davranış.
 */

import { LabelTemplate, PlaceholderData, getDesignSize, mmToDots } from '../../../types/labelTemplate.types';
import { drawLabel, preloadLabelAssets } from '../render/labelCanvasRenderer';
import { MonoBitmap } from './bmp1bit';

export interface RasterOptions {
  /**
   * Eşik (0-765). Kırmızı+yeşil+mavi toplamı bu değerin altındaysa mürekkep.
   * Antialiasing gri pikseller ürettiği için orta bir değer gerekiyor.
   */
  threshold?: number;
}

export interface RasterResult {
  mono: MonoBitmap;
  /** Fiziksel etiket üzerindeki dot ölçüsü (döndürme sonrası) */
  widthDots: number;
  heightDots: number;
}

/**
 * Mürekkep matrisini saat yönünde döndürür.
 * 90°: (x, y) → (h-1-y, x), sonuç ölçüsü h × w
 */
export function rotateMono(mono: MonoBitmap, degrees: 0 | 90 | 180 | 270): MonoBitmap {
  const { width: w, height: h, ink } = mono;
  if (degrees === 0) return mono;

  if (degrees === 180) {
    const out = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        out[(h - 1 - y) * w + (w - 1 - x)] = ink[y * w + x];
      }
    }
    return { width: w, height: h, ink: out };
  }

  // 90 ve 270'te en/boy yer değiştirir
  const outW = h;
  const outH = w;
  const out = new Uint8Array(outW * outH);

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const v = ink[y * w + x];
      if (v === 0) continue;
      if (degrees === 90) {
        out[x * outW + (h - 1 - y)] = v;
      } else {
        out[(w - 1 - x) * outW + y] = v;
      }
    }
  }

  return { width: outW, height: outH, ink: out };
}

/** Canvas'ı eşikleyip mürekkep matrisine çevirir */
export function canvasToMono(canvas: HTMLCanvasElement, threshold = 384): MonoBitmap {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D bağlamı alınamadı');

  const { width, height } = canvas;
  const data = ctx.getImageData(0, 0, width, height).data;
  const ink = new Uint8Array(width * height);

  for (let i = 0, p = 0; i < ink.length; i++, p += 4) {
    const alpha = data[p + 3];
    // Saydam alan boş kabul edilir; aksi halde şeffaf zemin siyah basılır
    if (alpha < 128) continue;
    if (data[p] + data[p + 1] + data[p + 2] < threshold) ink[i] = 1;
  }

  return { width, height, ink };
}

/**
 * Birden çok etiketi rasterler. Tuval bir kez kurulup her etiket için
 * yeniden kullanılır.
 */
export async function rasterizeLabels(
  template: LabelTemplate,
  dataList: Array<PlaceholderData | undefined>,
  options?: RasterOptions,
): Promise<RasterResult[]> {
  if (dataList.length === 0) return [];

  const design = getDesignSize(template);
  // Döndürme matris üzerinde yapılacağı için tasarım ölçüsünde çizilir
  const wDots = mmToDots(design.widthMm, template.dpi);
  const hDots = mmToDots(design.heightMm, template.dpi);
  const pxPerMm = template.dpi / 25.4;

  const canvas = document.createElement('canvas');
  canvas.width = wDots;
  canvas.height = hDots;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas 2D bağlamı alınamadı');

  await preloadLabelAssets(template, dataList);

  const results: RasterResult[] = [];

  for (const data of dataList) {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, wDots, hDots);

    drawLabel(ctx, template, data, { pxPerMm, skipRotation: true });

    const mono = canvasToMono(canvas, options?.threshold ?? 384);
    const rotated = rotateMono(mono, template.rotation);
    results.push({ mono: rotated, widthDots: rotated.width, heightDots: rotated.height });
  }

  canvas.width = 0;
  canvas.height = 0;

  return results;
}

/** Tek etiket için raster — tasarımcıdaki test baskısı bunu kullanıyor */
export async function rasterizeLabel(
  template: LabelTemplate,
  data: PlaceholderData | undefined,
  options?: RasterOptions,
): Promise<RasterResult> {
  const [result] = await rasterizeLabels(template, [data], options);
  if (!result) throw new Error('Etiket rasterlenemedi.');
  return result;
}

/** Şablonun fiziksel etikette kaplayacağı dot ölçüsü — doğrulama için */
export function expectedLabelDots(template: LabelTemplate): { widthDots: number; heightDots: number } {
  return {
    widthDots: mmToDots(template.labelWidthMm, template.dpi),
    heightDots: mmToDots(template.labelLengthMm, template.dpi),
  };
}
